"use client"
import { useTranslations } from "next-intl"

import type React from "react"
import { useState, useMemo, useEffect, useCallback, useRef } from "react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import type { StepProps } from "@/types/listing"
import { Truck, Plus, ChevronRight } from "lucide-react"
import { ShippingOptionItem } from "@/components/ui/shipping-option"
import { ShippingMethodDialog } from "@/components/ui/shipping-method-dialog"
import { DeleteShippingMethodDialog } from "@/components/ui/delete-shipping-method-dialog"
import { PAYPAL_SUPPORTED_COUNTRIES } from "@/lib/paypal-countries"
import { v4 as uuidv4 } from "uuid"
import { createClient } from "@/utils/supabase/client"
import { DEFAULT_CURRENCY } from "@/lib/currencies"
import { creationFlowButtonOutlineClass, creationFlowBlockClass, creationFlowSectionLabelClass } from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"
import { useToast } from "@/hooks/use-toast"
import {
  addShippingMethod,
  updateShippingMethod as updateShippingMethodApi,
  deleteShippingMethod as deleteShippingMethodApi,
  ShippingMethodError,
} from "@/lib/listings/shipping-methods-actions"
import {
  ShippingModeToggleAndParcelForm,
  isParcelComplete as isParcelCompleteSelector,
  type ParcelDimensions,
  type ShippingMode,
} from "@/components/shipping/ShippingModeToggleAndParcelForm"
import { type SupportTier } from "@/lib/shipping/origin-support"
import { getEffectiveTier } from "@/lib/shipping/country-access"
import { isCarrierAvailable } from "@/lib/shipping/carrier-eligibility"
import { ShipFromAddressPicker } from "@/components/shipping/ship-from-address-picker"
import { useShippingOrigin } from "@/hooks/use-shipping-origin"
import { useShippoBetaAccess } from "@/hooks/use-shippo-beta-access"

// Supabase client retained for the on-mount list-fetch + auth.getUser only.
// The 3 write helpers (insert/update/delete) were migrated onto the canonical
// `lib/listings/shipping-methods-actions.ts` builders in WS3.3.a — see
// `.tondocs/shippo/PLAN.md` §WS3.3.a. Closes the HARD-GATE Silent NACK on
// writes (`supabase.from(...)` inside `components/**`). The read path is
// out of WS3.3.a scope; it stays a direct `.select()` for now and is
// covered by the same builder migration in a follow-up.
const supabase = createClient()

// Update types to match your table columns – note "name" replaces "label"
export type ShippingOption = {
  id: string
  name: string
  description: string
  icon: React.ElementType
  price: string
  bannedCountries: string[]
  allowCombined?: boolean
  combinedFee?: string
}

export type NewShippingMethod = {
  name: string
  description: string
  price: string
  bannedCountries: string[]
}

// ADR_LISTING_SHIP_FROM_ADDRESS Phase 3 — `extractCountryCode(location)` was
// removed alongside `listings.location`. Origin country now comes from the
// shared `useShippingOrigin` hook (pin → seller's `is_main` fallback) so
// create + edit flows agree on a single resolution rule.

export function ShippingStep({ data, updateDataAction, onNextAction, hideButton, onValidationChange, onRegisterSave, compact }: StepProps) {
  const tLC = useTranslations("listingCreation");
  const { toast } = useToast()
  const currencyCode = data.currency || DEFAULT_CURRENCY
  const [selectedOptions, setSelectedOptions] = useState<string[]>(data.shipping?.methods || [])
  const [hasLoadedFromSupabase, setHasLoadedFromSupabase] = useState(false)
  // Default mode (WS3.1 Option D table): broad/limited tier → live_rate;
  // unsupported/null → legacy. Persisted value (`data.shippingMode`) wins
  // over the tier-default — sellers can downgrade to legacy on a Broad
  // origin and that choice sticks across step navigation.
  //
  // `data.ship_from_address_id` (and the resolved origin country) may resolve
  // after first render (StrictMode double-invoke / suspense), but
  // `useState(() => ...)` initializer runs ONCE. We re-sync via an effect
  // below when origin tier flips from null → known.
  const initialModeRef = useRef<ShippingMode | null>(null)
  const [shippingMode, setShippingMode] = useState<ShippingMode>(() => {
    if (data.shippingMode) {
      initialModeRef.current = data.shippingMode
      return data.shippingMode
    }
    // Tier may not be derivable yet on first render — fall back to legacy and
    // let the effect upgrade to live_rate once the country resolves.
    return "legacy"
  })
  // Canonical-only parcel state (slice C2): grams + mm. The Primitive
  // converts to/from locale on display; the persisted draft already carries
  // canonical units so re-entry into the step keeps the same values.
  const [parcel, setParcel] = useState<ParcelDimensions>(() => data.parcel ?? {
    weight: undefined,
    length: undefined,
    width: undefined,
    height: undefined,
  })
  
  const [customShippingOptions, setCustomShippingOptions] = useState<ShippingOption[]>(() => {
    if (data.shipping?.customOptions) {
      return data.shipping.customOptions.map((option: any) => ({
        id: option.id,
        name: option.label || option.name,
        description: option.description || "",
        icon: Truck,
        price: option.price?.toString() || "0",
        bannedCountries: option.bannedCountries || [],
        allowCombined: option.allowCombined ?? false,
        combinedFee: option.combinedFee?.toString() || "0",
      }))
    }
    return []
  })
  
  // Sync local state with data prop when it changes
  useEffect(() => {
    // Only update if we have saved data
    if (data.shipping?.methods && data.shipping.methods.length > 0) {
      setSelectedOptions(data.shipping.methods)
    }
    if (data.shipping?.customOptions && data.shipping.customOptions.length > 0) {
      setCustomShippingOptions(data.shipping.customOptions.map((option: any) => ({
        id: option.id,
        name: option.label || option.name,
        description: option.description || "",
        icon: Truck,
        price: option.price?.toString() || "0",
        bannedCountries: option.bannedCountries || [],
        allowCombined: option.allowCombined ?? false,
        combinedFee: option.combinedFee?.toString() || "0",
      })))
    }
  }, [data.shipping?.methods, data.shipping?.customOptions])

  const [dialogOpen, setDialogOpen] = useState(false)
  const [newShippingMethod, setNewShippingMethod] = useState<NewShippingMethod>({
    name: "",
    description: "",
    price: "",
    bannedCountries: [],
  })
  const [shippingMethodError, setShippingMethodError] = useState("")
  const [isLoadingMethods, setIsLoadingMethods] = useState(true)

  const [isEditing, setIsEditing] = useState(false)
  const [editingId, setEditingId] = useState<string | undefined>()

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | undefined>()

  // Per-listing ship-from override pin. Undefined ≡ "use my main address";
  // a uuid ≡ the seller picked a non-main saved address. ADR_LISTING_SHIP_FROM_ADDRESS.md.
  // Hoisted above `useShippingOrigin` so the hook receives the stable
  // callback as its `onShipFromAddressChange` arg.
  const handleShipFromAddressChange = useCallback(
    (next: string | null) => {
      updateDataAction({ ship_from_address_id: next ?? undefined })
    },
    [updateDataAction],
  )

  // Single resolver shared with EditShippingTab (edit-listing-dialog). Pin
  // (`data.ship_from_address_id`) wins; otherwise falls back to the seller's
  // `is_main` address. Returns `binding: null` while the address-book fetch
  // is in-flight — the tier-default effect below treats that the same as
  // "no origin known" (legacy mode), then upgrades once the binding resolves.
  const { binding: shippingOriginBinding } = useShippingOrigin({
    shipFromAddressId: data.ship_from_address_id ?? null,
    onShipFromAddressChange: handleShipFromAddressChange,
  })
  const baseCountry: string | undefined = shippingOriginBinding?.country

  // Per-seller beta enrolment for live-rate in non-GA countries (GB/DE/FR).
  // Fail-closed during load — `getEffectiveTier` collapses `beta`-tier
  // origins to "unsupported" when the flag is false, so the UI never
  // promises live-rate the server would reject.
  const { hasBetaAccess: hasShippoBetaAccess } = useShippoBetaAccess()

  // WS3.1 (Option D, ORIGIN_COUNTRY_SUPPORT.md §5.1) — tier feeds the
  // Primitive's live_rate gating. Composed via `getEffectiveTier`
  // (capability + access + beta-flag); see lib/shipping/country-access.ts.
  // `undefined` ISO = seller hasn't set a location yet → `null` (Primitive
  // renders "set up address" prompt). ISO present but not in
  // SHIPPO_ORIGIN_SUPPORT, or in a `beta`-country without enrolment →
  // "unsupported".
  const originTier: SupportTier | null = useMemo(() => {
    return getEffectiveTier(baseCountry, hasShippoBetaAccess)
  }, [baseCountry, hasShippoBetaAccess])

  // Display label for Limited-tier disclosure — fall back to ISO if name
  // lookup misses (PAYPAL_SUPPORTED_COUNTRIES carries the canonical name).
  const originCountryLabel = useMemo(() => {
    if (!baseCountry) return null
    const match = PAYPAL_SUPPORTED_COUNTRIES.find((c) => c.code === baseCountry)
    return match?.name ?? baseCountry
  }, [baseCountry])

  // Carrier currency-mismatch gate (ADR_CURRENCY_PIN, 2026-06-23). Profile
  // currency lives in `currencyCode` (= `data.currency`); ship-from country
  // is `baseCountry`. When the listing's currency ≠ native_currency(country)
  // — the Buffon case (USD store shipping from ES) — disable live-rate and
  // render an explainer. The seller can either change their store currency
  // (deferred — service-role support path) or pin a US ship-from address.
  const currencyMismatchExplainer = useMemo<string | null>(() => {
    if (!baseCountry) return null
    const eligibility = isCarrierAvailable(
      { currency: currencyCode, shipFromCountry: baseCountry },
      "shippo",
      { hasBetaAccess: hasShippoBetaAccess },
    )
    if (eligibility.ok) return null
    if (eligibility.reason !== "CURRENCY_MISMATCH") return null
    const country = originCountryLabel ?? baseCountry
    return (
      `Your store is in ${currencyCode} but you ship from ${country} — ` +
      `live-rate shipping is unavailable. Either update your shipping origin ` +
      `to a US address or use flat-rate methods.`
    )
  }, [baseCountry, currencyCode, originCountryLabel, hasShippoBetaAccess])

  // Tier-default upgrade: once `originTier` resolves to broad/limited and the
  // user hasn't persisted a choice yet (no `data.shippingMode`), promote mode
  // to `live_rate` (the spec default for those tiers). Skipped once the user
  // has interacted — `initialModeRef` is set on every persisted-driven init,
  // and `setShippingMode` from user toggling also sets it via setMode below.
  useEffect(() => {
    if (initialModeRef.current !== null) return
    if (data.shippingMode) return
    if (originTier === "broad" || originTier === "limited") {
      initialModeRef.current = "live_rate"
      setShippingMode("live_rate")
    } else if (originTier === "unsupported" || originTier === null) {
      initialModeRef.current = "legacy"
    }
  }, [originTier, data.shippingMode])

  const isParcelComplete = isParcelCompleteSelector({ shippingMode, parcel })

  // Notify parent about validation status. Legacy → ≥1 method selected;
  // live_rate → all 6 parcel dims present + positive (mass/distance units
  // have sensible defaults so they're always set).
  useEffect(() => {
    if (shippingMode === "live_rate") {
      onValidationChange?.(isParcelComplete)
    } else {
      onValidationChange?.(selectedOptions.length > 0)
    }
  }, [selectedOptions.length, onValidationChange, shippingMode, isParcelComplete])

  const allShippingOptions = customShippingOptions

  // Fetch existing shipping methods for the authenticated user on mount
  useEffect(() => {
    async function fetchShippingMethods() {
      
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser()

      if (userError || !user) {
        console.error("❌ User not authenticated", userError)
        setIsLoadingMethods(false)
        return
      }

      const { data: methodsData, error } = await supabase
        .from("shipping_methods")
        .select("*")
        .eq("user_id", user.id)

      if (error) {
        console.error("❌ Error fetching shipping methods", error)
        setIsLoadingMethods(false)
      } else if (methodsData) {
        
        // Map the fetched methods to ShippingOption type
        const fetchedOptions: ShippingOption[] = methodsData.map((m: any) => ({
          id: m.id,
          name: m.name,
          description: m.description,
          icon: Truck,
          price: m.price,
          bannedCountries: m.banned_countries,
          allowCombined: false,
          combinedFee: "0",
        }))

        // If we have saved data, merge it with fetched options
        if (data.shipping?.customOptions && data.shipping.customOptions.length > 0) {
          // Keep the saved data's allowCombined and combinedFee settings
          const mergedOptions = fetchedOptions.map(fetchedOption => {
            const savedOption = data.shipping.customOptions.find((opt: any) => opt.id === fetchedOption.id)
            if (savedOption) {
              return {
                ...fetchedOption,
                allowCombined: savedOption.allowCombined ?? false,
                combinedFee: savedOption.combinedFee?.toString() || "0",
              }
            }
            return fetchedOption
          })
          setCustomShippingOptions(mergedOptions)
          // Keep the saved selection
          if (data.shipping?.methods && data.shipping.methods.length > 0) {
            setSelectedOptions(data.shipping.methods)
          }
        } else {
          // First time loading - set all options and select them all
          setCustomShippingOptions(fetchedOptions)
          const selectedIds = fetchedOptions.map((option) => option.id)
          setSelectedOptions(selectedIds)
        }
        
        setIsLoadingMethods(false)
      }
    }
    
    // Only fetch if we haven't loaded yet
    if (!hasLoadedFromSupabase) {
      fetchShippingMethods()
      setHasLoadedFromSupabase(true)
    }
  }, [hasLoadedFromSupabase, data.shipping])

  const toggleOption = (id: string) => {
    setSelectedOptions((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    )
    if (navigator.vibrate) navigator.vibrate(10)
  }

  // These functions for allowCombined and combinedFee remain for UI state (not sent to DB)
  const toggleCombinedShipping = (id: string) => {
    setCustomShippingOptions((prev) =>
      prev.map((option) =>
        option.id === id ? { ...option, allowCombined: !option.allowCombined } : option,
      ),
    )
  }

  const updateCombinedFee = (id: string, fee: string) => {
    const updatedFee = fee === "" ? "0" : fee
    setCustomShippingOptions((prev) =>
      prev.map((option) => (option.id === id ? { ...option, combinedFee: updatedFee } : option)),
    )
  }

  const handleAddShippingMethod = async () => {
    if (!newShippingMethod.name.trim() || !newShippingMethod.price.trim()) return
    if (newShippingMethod.bannedCountries.length === PAYPAL_SUPPORTED_COUNTRIES.length) {
      setShippingMethodError("You must allow at least one country for shipping.")
      return
    }

    const newId = uuidv4()
    const newOption: ShippingOption = {
      id: newId,
      name: newShippingMethod.name, // Use "name" to match DB column
      description: newShippingMethod.description || "",
      icon: Truck,
      price: newShippingMethod.price,
      bannedCountries: newShippingMethod.bannedCountries,
      allowCombined: false,
      combinedFee: "0",
    }

    // Mettre à jour les états
    const updatedOptions = [...customShippingOptions, newOption]
    const updatedSelected = [...selectedOptions, newId]
    
    setCustomShippingOptions(updatedOptions)
    setSelectedOptions(updatedSelected)
    
    // Sauvegarder immédiatement les données dans le parent pour s'assurer que la nouvelle méthode est incluse
    const updatedOptionsForSave = updatedOptions.map((option) => ({
      id: option.id,
      label: option.name,
      price: parseFloat(option.price),
      allowCombined: option.allowCombined,
      combinedFee: parseFloat(option.combinedFee ?? "0"),
    }))
    
    updateDataAction({
      shipping: {
        methods: updatedSelected,
        customOptions: updatedOptionsForSave,
      },
    })
    
    try {
      await addShippingMethod({
        id: newOption.id,
        name: newOption.name,
        description: newOption.description,
        price: newOption.price,
        banned_countries: newOption.bannedCountries,
        currency: currencyCode,
      })
    } catch (err) {
      const description = err instanceof ShippingMethodError
        ? (err.serverError ?? "Failed to add shipping method.")
        : "Failed to add shipping method."
      toast({ title: "Error", description, variant: "destructive" })
    }
    resetForm()
    if (navigator.vibrate) navigator.vibrate([10, 20, 10])
  }

  const handleEditShippingMethod = (id: string) => {
    const option = customShippingOptions.find((opt) => opt.id === id)
    if (!option) return
    setNewShippingMethod({
      name: option.name,
      description: option.description,
      price: option.price,
      bannedCountries: option.bannedCountries,
    })
    setIsEditing(true)
    setEditingId(id)
    setDialogOpen(true)
  }

  const handleUpdateShippingMethod = async (id: string) => {
    if (!newShippingMethod.name.trim() || !newShippingMethod.price.trim()) return
    if (newShippingMethod.bannedCountries.length === PAYPAL_SUPPORTED_COUNTRIES.length) {
      setShippingMethodError("You must allow at least one country for shipping.")
      return
    }

    const updatedOption: ShippingOption = {
      id,
      name: newShippingMethod.name,
      description: newShippingMethod.description,
      icon: Truck,
      price: newShippingMethod.price,
      bannedCountries: newShippingMethod.bannedCountries,
      allowCombined: false,
      combinedFee: "0",
    }

    setCustomShippingOptions((prev) =>
      prev.map((option) => (option.id === id ? updatedOption : option)),
    )
    try {
      await updateShippingMethodApi(id, {
        name: updatedOption.name,
        description: updatedOption.description,
        price: updatedOption.price,
        banned_countries: updatedOption.bannedCountries,
        currency: currencyCode,
      })
    } catch (err) {
      const description = err instanceof ShippingMethodError
        ? (err.serverError ?? "Failed to update shipping method.")
        : "Failed to update shipping method."
      toast({ title: "Error", description, variant: "destructive" })
    }
    resetForm()
    if (navigator.vibrate) navigator.vibrate([10, 20, 10])
  }

  const handleDeleteShippingMethod = (id: string) => {
    setDeletingId(id)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!deletingId) return
    setCustomShippingOptions((prev) => prev.filter((option) => option.id !== deletingId))
    setSelectedOptions((prev) => prev.filter((id) => id !== deletingId))
    setDeleteDialogOpen(false)
    try {
      await deleteShippingMethodApi(deletingId)
    } catch (err) {
      const description = err instanceof ShippingMethodError
        ? (err.serverError ?? "Failed to delete shipping method.")
        : "Failed to delete shipping method."
      toast({ title: "Error", description, variant: "destructive" })
    }
    setDeletingId(undefined)
    if (navigator.vibrate) navigator.vibrate([10, 20, 10])
  }

  const resetForm = () => {
    setNewShippingMethod({ name: "", description: "", price: "", bannedCountries: [] })
    setShippingMethodError("")
    setDialogOpen(false)
    setIsEditing(false)
    setEditingId(undefined)
  }

  const saveData = useCallback(() => {
    const updatedOptions = customShippingOptions.map((option) => ({
      id: option.id,
      label: option.name,
      price: parseFloat(option.price),
      allowCombined: option.allowCombined,
      combinedFee: parseFloat(option.combinedFee ?? "0"),
    }))

    updateDataAction({
      shipping: {
        // live_rate ⇒ no flat methods, ever — belt to the onChange purge's
        // suspenders, so a stale selection can't reach shipping_config.
        methods: shippingMode === "live_rate" ? [] : selectedOptions,
        customOptions: updatedOptions,
      },
      shippingMode,
      parcel: shippingMode === "live_rate" ? parcel : undefined,
    })
  }, [customShippingOptions, selectedOptions, updateDataAction, shippingMode, parcel])

  // Register save function
  useEffect(() => {
    onRegisterSave?.(saveData)
    return () => {
      onRegisterSave?.(null)
    }
  }, [saveData, onRegisterSave])

  const handleNext = () => {
    saveData()
    onNextAction()
  }

  const sectionLabel = creationFlowSectionLabelClass
  const cardRadius = "rounded-3xl"
  const emptyCardBase = creationFlowBlockClass

  return (
    <div className={compact ? "space-y-4 px-1" : "space-y-6 px-2"}>
      <motion.div className="space-y-4" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        {/* Shippo live-rate UI is behind the per-seller `beta_features` flag */}
        {/* (2026-06-28 — US/GB/DE/FR all beta). Without it, the seller sees */}
        {/* only the flat-rate list below — same as pre-Shippo. The flag is */}
        {/* fetched via `useShippoBetaAccess`; on dev set `BETA_FEATURES_BYPASS` */}
        {/* in .env.local to grant it for every seller without a DB INSERT. */}
        {hasShippoBetaAccess && (
          <ShippingModeToggleAndParcelForm
            value={{ shippingMode, parcel }}
            onChange={(next) => {
              // Modes are mutually exclusive: a live_rate listing quotes the
              // carrier at checkout, so flipping to live_rate DESELECTS any
              // manually-picked flat methods — a stale selection must never
              // ride into `shipping_config` (saveData enforces the same).
              if (next.shippingMode === "live_rate" && shippingMode !== "live_rate") {
                setSelectedOptions([])
              }
              setShippingMode(next.shippingMode)
              setParcel(next.parcel)
            }}
            sellerCountry={baseCountry ?? ""}
            compact={compact}
            originTier={originTier}
            originCountryLabel={originCountryLabel}
            currencyMismatchExplainer={currencyMismatchExplainer}
          />
        )}

        <ShipFromAddressPicker
          value={data.ship_from_address_id ?? null}
          onChange={handleShipFromAddressChange}
          compact={compact}
        />

        <p className={cn(sectionLabel, "px-0.5")}>{tLC("shippingMethods")}</p>
        {isLoadingMethods ? (
          <div className={cn("flex h-32 items-center justify-center", cardRadius, emptyCardBase, "p-4")}>
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-sheet-handle border-t-muted-foreground" />
          </div>
        ) : (
          <>
            {allShippingOptions.length === 0 ? (
              <div className={cn("text-center py-10 px-4", cardRadius, emptyCardBase)}>
                <Truck className="w-12 h-12 mx-auto mb-4 text-muted-foreground/70" />
                <p className="font-medium mb-1 text-foreground">No shipping methods yet</p>
                <p className="text-sm text-muted-foreground">Add your first shipping method to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {allShippingOptions.map((option, index) => (
                  <motion.div key={option.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                    <ShippingOptionItem
                      option={option}
                      currencyCode={currencyCode}
                      isSelected={selectedOptions.includes(option.id)}
                      onToggle={toggleOption}
                      onToggleCombined={toggleCombinedShipping}
                      onUpdateCombinedFee={updateCombinedFee}
                      onEdit={handleEditShippingMethod}
                      onDelete={handleDeleteShippingMethod}
                      variant="dark"
                    />
                  </motion.div>
                ))}
              </div>
            )}

            <ShippingMethodDialog
              open={dialogOpen}
              onOpenChangeAction={(open) => {
                setDialogOpen(open)
                if (!open) resetForm()
              }}
              newShippingMethod={newShippingMethod}
              setNewShippingMethodAction={setNewShippingMethod}
              onAddMethodAction={handleAddShippingMethod}
              onUpdateMethod={handleUpdateShippingMethod}
              error={shippingMethodError}
              baseCountry={baseCountry}
              isEditing={isEditing}
              editingId={editingId}
              currencyCode={currencyCode}
            />

            <Button
              variant="outline"
              className={cn(
                compact ? "w-full py-4 text-sm" : "w-full py-5 text-base",
                "flex items-center justify-center gap-2 rounded-2xl border-dashed",
                creationFlowButtonOutlineClass
              )}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setDialogOpen(true)
              }}
            >
              <Plus className="w-5 h-5" />
              Add Shipping Method
            </Button>
          </>
        )}
      </motion.div>

      <DeleteShippingMethodDialog
        open={deleteDialogOpen}
        onOpenChangeAction={setDeleteDialogOpen}
        shippingMethodName={
          deletingId
            ? customShippingOptions.find((option) => option.id === deletingId)?.name || "Unknown"
            : ""
        }
        onConfirmAction={confirmDelete}
      />

      {!hideButton && (
        <div className="pt-6">
          <Button onClick={handleNext} disabled={selectedOptions.length === 0} className={`w-full ${compact ? "h-14 text-sm shadow-md" : "h-16 text-base shadow-lg"} rounded-full group`} size={compact ? "default" : "lg"}>
            Continue
            <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      )}

    </div>
  )
}